import { queryKeys } from '@tms/api-client';
import { formatCalendarDate, formatIstWeekdayDate, istHour } from '@tms/core';
import {
  AppBar,
  BalanceCard,
  Banner,
  Card,
  EmptyState,
  EntryRow,
  EntryRowSkeleton,
  Fab,
  NavBar,
  PhoneShell,
  Pill,
  defaultNavItems,
} from '@tms/ui';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

import { getMe, getSummary } from '../../api';
import type { LedgerEntry } from '../../api';
import { pathToEntry, routePaths } from '../../routes/routes';
import { homeCopy } from './copy';
import styles from './HomeScreen.module.css';

/**
 * `LED-01` — the screen people open the app to.
 *
 * Balance first, four recent entries, three shortcuts, and a trial strip that is honest about the count
 * and blocks nothing. That order is the screen book's and it is the product's argument in miniature: the
 * number you came for, then what made it, then everything else.
 *
 * **Two queries, not one.** The summary is `GET /me/summary`, which is one round trip by design
 * (`FEAT-011 §5.7`); the trial date is on `GET /me`, where `ONB-07` already reads it (`FEAT-008 §3.4`).
 * Duplicating `trialEndsOn` into the summary would give one promise two sources that could disagree, and
 * the account is cached for the whole session anyway — this is a second request on a cold start and none
 * after it.
 *
 * It is **not** lazy (`STD-002 §8`): Home is the first thing anyone sees, and loading it on demand only
 * moves the wait somewhere more annoying.
 */
export function HomeScreen(): React.JSX.Element {
  const navigate = useNavigate();

  const summary = useQuery({
    queryKey: queryKeys.ledger.balance(),
    queryFn: async () => {
      const result = await getSummary();
      if (!result.ok) {
        throw result.failure;
      }

      return result.value;
    },
  });

  const account = useQuery({
    queryKey: queryKeys.session.current(),
    queryFn: async () => {
      const result = await getMe();
      if (!result.ok) {
        throw result.failure;
      }

      return result.value;
    },
  });

  const recent = summary.data?.recent ?? [];

  // One instant for the whole render, so the greeting and its date cannot come from two different
  // milliseconds — which is a real difference at 23:59:59.999 IST.
  const now = new Date();

  return (
    <PhoneShell
      appBar={
        <AppBar
          title={homeCopy.wordmark}
          subtitle={homeCopy.greeting(istHour(now), account.data?.displayName, formatIstWeekdayDate(now))}
        />
      }
      fab={
        <Fab
          label={homeCopy.addEntry}
          data-testid="add-entry"
          onPress={() => {
            void navigate(routePaths.addEntry);
          }}
        />
      }
      navBar={
        <NavBar
          items={defaultNavItems}
          currentId="home"
          onNavigate={(next) => {
            // Reports and Settings arrive with their own tasks. A tab that navigated to a screen that does
            // not exist would be worse than one that does nothing yet.
            if (next === 'ledger') {
              void navigate(routePaths.ledger);
            }
          }}
        />
      }
    >
      <BalanceCard
        // Optional all the way down, not just on `data`. A response that arrived without a month would
        // otherwise take the whole app down with a `TypeError` rather than rendering the eyebrow without
        // one — found by `T-0214`'s test, whose stub answers this endpoint with an empty object.
        eyebrow={
          summary.data?.month
            ? `${homeCopy.balanceEyebrow} · ${summary.data.month.label}`
            : homeCopy.balanceEyebrow
        }
        balanceMinor={summary.data?.balanceMinor ?? 0}
        creditMinor={summary.data?.creditMinor ?? 0}
        debitMinor={summary.data?.debitMinor ?? 0}
        loading={summary.isPending}
        // Spread rather than `error={... : undefined}`: `exactOptionalPropertyTypes` treats an explicit
        // `undefined` as a value, and the prop is meant to be absent when there is nothing wrong.
        {...(summary.isError ? { error: homeCopy.summaryFailed } : {})}
      />

      <div className={styles.shortcuts}>
        {homeCopy.shortcuts.map((shortcut) => (
          <Pill key={shortcut.id}>{shortcut.label}</Pill>
        ))}
      </div>

      <div className={styles.head}>
        <h2 className={styles.heading}>{homeCopy.recentHeading}</h2>
        <span className={styles.link}>{homeCopy.fullLedger}</span>
      </div>

      {summary.isPending ? (
        <Card flush>
          <EntryRowSkeleton count={4} />
        </Card>
      ) : recent.length === 0 ? (
        <EmptyState title={homeCopy.noEntriesTitle} body={homeCopy.noEntriesBody} />
      ) : (
        <Card flush>
          {recent.map((entry) => (
            <EntryRow
              key={entry.id}
              icon={entry.category.icon}
              title={titleOf(entry)}
              detail={detailOf(entry)}
              amountMinor={entry.amountMinor}
              kind={entry.kind === 'Credit' ? 'credit' : 'debit'}
              onPress={() => {
                void navigate(pathToEntry(entry.id));
              }}
            />
          ))}
        </Card>
      )}

      {account.data?.trialEndsOn ? (
        <div className={styles.trial}>
          <Banner tone="info">
            <b>{homeCopy.trialHeadline(daysUntil(account.data.trialEndsOn))}</b>
            {homeCopy.trialBody}
          </Banner>
        </div>
      ) : null}
    </PhoneShell>
  );
}

/**
 * What the row calls the entry: the note if there is one, and the purpose if there is not.
 *
 * `LED-01` shows "Weekly sabzi & rations" over "Grocery · Today", which is the note over the purpose. An
 * entry with no note would leave the top line empty, so the purpose moves up into it — the row keeps its
 * shape and nothing renders as a blank.
 */
function titleOf(entry: LedgerEntry): string {
  return entry.note?.trim() ? entry.note : entry.category.name;
}

/** "Grocery · 16 Aug", or just the date when the purpose has already been used as the title. */
function detailOf(entry: LedgerEntry): string {
  const date = formatCalendarDate(entry.occurredOn);

  return entry.note?.trim() ? `${entry.category.name} · ${date}` : date;
}

/**
 * Whole days from today to the end of the trial, floored at zero.
 *
 * Computed here rather than by the server, and that is not a contradiction of `FEAT-008 §3.4`: the *date*
 * comes from the server and is never derived on a device, because it is a promise about money. How many
 * days away that date is depends on what day it is where the person is standing, which is the one part of
 * this only the client can know.
 */
function daysUntil(endsOn: string): number {
  const end = new Date(`${endsOn}T00:00:00+05:30`).getTime();
  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();

  return Math.max(0, Math.ceil((end - startOfToday) / 86_400_000));
}
